<!--
  직원 화면 미리보기 — 배포 전에 "이 직원에게 실제로 어떻게 보이는가"를 관리자 화면에서 확인.
  MyContractDetailView와 같은 구성(정보 → 문서 → 열람 요건 → 동의 문구/버튼)을 그대로 그리되
  동의·거부·열람 기록은 절대 발생하지 않는다(버튼 비활성, API 호출 없음).
-->
<template>
  <el-dialog v-model="visible" title="직원 화면 미리보기" width="900px" top="4vh" destroy-on-close>
    <el-alert type="warning" :closable="false" show-icon class="pv-banner">
      <template #title>
        미리보기입니다 — 여기서는 열람 기록도, 동의도 발생하지 않습니다. 실제 화면은 직원이 "내 계약서" 메뉴에서 봅니다.
      </template>
    </el-alert>

    <div class="pv-toolbar">
      <span class="pv-label">보는 직원</span>
      <el-select v-model="partyId" placeholder="대상자 선택" style="width: 260px" filterable>
        <el-option
          v-for="p in parties"
          :key="p.id"
          :value="p.id"
          :label="`${p.employee_name || p.employee_username}${p.department_name ? ' · ' + p.department_name : ''}${contract.distribute_type === 'INDIVIDUAL' && !p.individual_file ? ' (파일 없음)' : ''}`"
        />
      </el-select>
      <el-tag v-if="contract.distribute_type === 'INDIVIDUAL'" type="warning" size="small">개별 문서 — 직원마다 다른 파일</el-tag>
      <el-tag v-else type="info" size="small">공통 문서 — 전원 같은 파일</el-tag>
    </div>

    <!-- 배포 전 점검표 -->
    <div class="pv-checks">
      <div v-for="c in checks" :key="c.text" class="pv-check" :class="c.ok ? 'ok' : 'bad'">
        <el-icon><component :is="c.ok ? CircleCheckFilled : WarningFilled" /></el-icon>
        <span>{{ c.text }}</span>
      </div>
    </div>

    <el-divider content-position="left">▼ 아래부터 직원에게 보이는 화면</el-divider>

    <div class="pv-screen">
      <!-- 계약서 정보 (직원 상세와 동일) -->
      <el-card class="info-card">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="계약번호">{{ contract.contract_number }}</el-descriptions-item>
          <el-descriptions-item label="상태"><el-tag type="info">미확인</el-tag></el-descriptions-item>
          <el-descriptions-item label="계약서명">{{ contract.title }}</el-descriptions-item>
          <el-descriptions-item label="확인기한">{{ contract.deadline?.substring(0, 16) }}</el-descriptions-item>
        </el-descriptions>
      </el-card>

      <!-- 계약 조건 -->
      <el-card v-if="termsRows.length" class="info-card">
        <template #header><h3>계약 조건</h3></template>
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item v-for="r in termsRows" :key="r.key" :label="r.label">{{ r.value || '(미입력)' }}</el-descriptions-item>
        </el-descriptions>
      </el-card>

      <!-- 문서 -->
      <el-card class="info-card">
        <template #header><h3>계약서 내용</h3></template>
        <div v-if="docUrl" class="pv-docinfo" :class="docCheck.level">
          <el-icon><component :is="docCheck.level === 'ok' ? CircleCheckFilled : WarningFilled" /></el-icon>
          <span>{{ docCheck.text }}</span>
          <el-link :href="docUrl" target="_blank" type="primary" style="margin-left:auto">새 탭에서 열기</el-link>
        </div>
        <iframe v-if="docUrl && docCheck.level !== 'bad' && blobUrl" :src="blobUrl" class="pv-doc" />
        <div v-else-if="docUrl" class="pv-nofile">문서를 화면 안에 표시할 수 없습니다. 위 "새 탭에서 열기"로 확인하세요.</div>
        <div v-else class="pv-nofile">계약서 파일이 아직 준비되지 않았습니다. — 직원에게도 이렇게 보입니다. 배포 전에 파일을 올리세요.</div>
        <div class="pv-readstatus">
          <el-tag type="info" size="small">열람 중...</el-tag>
          <span class="pv-readtime">열람 시간: 0초 / {{ contract.template_minimum_read_seconds ?? 0 }}초</span>
          <el-button v-if="contract.template_require_full_read" type="text" size="small" disabled>전체 확인 완료</el-button>
        </div>
      </el-card>

      <!-- 동의 -->
      <el-card class="info-card">
        <template #header><h3>동의</h3></template>
        <div v-for="(item, idx) in consentItems" :key="idx" class="pv-consent-item">
          <el-checkbox v-model="checked[idx]">
            {{ item.label }}
            <el-tag v-if="item.required" type="danger" size="small" style="margin-left:4px">필수</el-tag>
          </el-checkbox>
        </div>
        <div v-if="consentItems.length === 0" class="pv-nofile">동의 문구가 없습니다 — 양식 관리에서 동의 항목을 넣으세요.</div>
        <div v-if="contract.template_require_signature" class="pv-sig">
          <div class="pv-sig-title">서명 <el-tag type="danger" size="small">필수</el-tag></div>
          <div class="pv-sig-box">직원이 여기에 자필 서명합니다 (마우스·터치)</div>
          <div class="pv-hint">동의 후 회사 직인 + 직원 서명 + 동의 기록이 담긴 완성본 PDF가 자동 생성되어 직원에게 교부됩니다.</div>
        </div>
        <el-alert type="warning" :closable="false" class="pv-errors">
          <ul class="pv-errlist">
            <li v-if="contract.template_require_full_read">문서를 끝까지 읽고 [전체 확인 완료]를 눌러야 동의할 수 있습니다.</li>
            <li v-if="(contract.template_minimum_read_seconds ?? 0) > 0">최소 {{ contract.template_minimum_read_seconds }}초 이상 열람해야 동의할 수 있습니다.</li>
            <li v-if="!contract.template_require_full_read && !(contract.template_minimum_read_seconds > 0)">열람 요건이 없어 문서를 열자마자 동의할 수 있습니다.</li>
          </ul>
        </el-alert>
        <div class="pv-actions">
          <el-button type="primary" size="large" disabled>동의합니다</el-button>
          <el-button type="danger" size="large" plain disabled>거부</el-button>
        </div>
      </el-card>
    </div>

    <template #footer>
      <el-button @click="visible = false">닫기</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { CircleCheckFilled, WarningFilled } from "@element-plus/icons-vue";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  contract: { type: Object, required: true },   // ContractDetail (template_* 필드 포함)
  parties: { type: Array, default: () => [] },   // 대상자 목록 (individual_file은 URL)
});
const emit = defineEmits(["update:modelValue"]);

const visible = computed({ get: () => props.modelValue, set: (v) => emit("update:modelValue", v) });
const partyId = ref(null);
const checked = ref({});

watch(visible, (v) => {
  if (v) {
    // 처음 열 때: 파일 없는 직원이 있으면 그 사람을 먼저 보여준다 (문제를 먼저 보게)
    const missing = props.parties.find((p) => props.contract.distribute_type === "INDIVIDUAL" && !p.individual_file);
    partyId.value = (missing || props.parties[0])?.id ?? null;
    checked.value = {};
  }
});

const party = computed(() => props.parties.find((p) => p.id === partyId.value) || null);
const docUrl = computed(() =>
  props.contract.distribute_type === "INDIVIDUAL" ? party.value?.individual_file || "" : props.contract.document_file || ""
);
// 문서가 실제로 열리는지 사전 점검 — 응답 코드·형식·iframe 허용 여부를 눈에 보이게 (배포 전 확인 + 장애 진단)
const docCheck = ref({ level: "info", text: "문서 확인 중…" });
// 받아온 파일을 blob URL로 iframe에 넣는다 — 서버의 X-Frame-Options/프록시 환경과 무관하게 표시됨
const blobUrl = ref("");
function releaseBlob() {
  if (blobUrl.value) URL.revokeObjectURL(blobUrl.value);
  blobUrl.value = "";
}
watch(visible, (v) => { if (!v) releaseBlob(); });
watch(docUrl, async (url) => {
  releaseBlob();
  if (!url) return;
  docCheck.value = { level: "info", text: "문서 확인 중…" };
  try {
    const r = await fetch(url, { method: "GET", credentials: "same-origin", cache: "no-store" });
    const ct = (r.headers.get("content-type") || "").split(";")[0].trim();
    if (r.ok && ct === "application/pdf") {
      const blob = await r.blob();
      blobUrl.value = URL.createObjectURL(blob);
    }
    const xfo = r.headers.get("x-frame-options");
    const name = decodeURIComponent(url.split("/").pop() || "");
    if (!r.ok) {
      docCheck.value = { level: "bad", text: `문서를 불러올 수 없습니다 (HTTP ${r.status}) — ${name}. 서버에 파일이 없거나 /media 경로가 막혀 있습니다.` };
    } else if (xfo && /deny/i.test(xfo)) {
      docCheck.value = { level: "warn", text: `여기서는 보이지만 직원 화면(내 계약서)에서는 문서가 막힐 수 있습니다 (서버 응답 X-Frame-Options: ${xfo}) — 백엔드 X_FRAME_OPTIONS=SAMEORIGIN 확인 필요.` };
    } else if (ct === "application/pdf") {
      docCheck.value = { level: "ok", text: `PDF 정상 (HTTP ${r.status}, ${name})` };
    } else if (/wordprocessingml|msword|hwp|octet-stream/.test(ct) || /\.(docx?|hwpx?)$/i.test(name)) {
      docCheck.value = { level: "warn", text: `${name} — 브라우저는 이 형식(${ct || "문서 파일"})을 화면 안에 그리지 못합니다. 직원은 다운로드해서 열어야 하며 열람 추적이 약해집니다. PDF로 변환해 올리기를 권합니다.` };
    } else {
      docCheck.value = { level: "warn", text: `응답 형식 ${ct || "알 수 없음"} (HTTP ${r.status}) — PDF가 아니면 화면 안에 표시되지 않을 수 있습니다.` };
    }
  } catch (e) {
    docCheck.value = { level: "bad", text: `문서 요청 실패: ${e.message} — 주소 ${url}` };
  }
}, { immediate: true });

// 계약 조건 표 — 서버 포맷(terms_summary)은 요약뿐이라 화면에선 원값을 간단 포맷
const termsRows = computed(() => {
  const fields = props.contract.template_term_fields || [];
  const vals = party.value?.terms || {};
  return fields.map((f) => {
    const raw = vals[f.key];
    let v = raw ?? "";
    if (v !== "" && (f.type === "money" || f.type === "number")) {
      const n = Number(String(v).replace(/[,\s원]/g, "")); if (Number.isFinite(n)) v = n.toLocaleString() + (f.unit || (f.type === "money" ? "원" : ""));
    }
    return { key: f.key, label: f.label, value: String(v) };
  });
});

const consentItems = computed(() =>
  (props.contract.template_consent_items || []).map((c) => (typeof c === "string" ? { label: c, required: true } : c))
);

// 배포 전 점검표 — 직원이 막히는 지점을 관리자가 먼저 보게
const checks = computed(() => {
  const c = props.contract;
  const missing = c.distribute_type === "INDIVIDUAL" ? props.parties.filter((p) => !p.individual_file).length : 0;
  const list = [];
  if (c.distribute_type === "INDIVIDUAL")
    list.push({ ok: missing === 0, text: missing === 0 ? `개별 파일 ${props.parties.length}명 전원 첨부됨` : `개별 파일 없는 직원 ${missing}명 — 그 직원은 문서 없는 빈 화면을 봅니다` });
  else list.push({ ok: !!c.document_file, text: c.document_file ? "공통 계약서 파일 첨부됨" : "공통 계약서 파일이 없습니다" });
  list.push({ ok: consentItems.value.length > 0, text: consentItems.value.length > 0 ? `동의 문구 ${consentItems.value.length}개` : "동의 문구가 없습니다" });
  if ((c.template_term_fields || []).length) {
    const bad = props.parties.filter((p) => !p.terms_complete);
    const detail = bad.slice(0, 3).map((p) => `${p.employee_name || p.employee_username}: ${(p.terms_errors || []).map((e) => e.replace(/은\(는\) 필수입니다\./, "")).join("·") || "미입력"}`).join(" / ");
    list.push({ ok: bad.length === 0, text: bad.length === 0 ? "계약 조건 전원 입력됨" : `계약 조건 누락 ${bad.length}명 — ${detail}${bad.length > 3 ? " …" : ""}` });
  }
  list.push({ ok: props.parties.length > 0, text: props.parties.length > 0 ? `대상자 ${props.parties.length}명` : "대상자가 없습니다" });
  const dd = c.days_until_deadline;
  list.push({ ok: dd == null || dd >= 1, text: dd == null ? "확인기한 미정" : dd < 0 ? `확인기한이 이미 ${-dd}일 지났습니다` : dd === 0 ? "확인기한이 오늘입니다 — 직원이 읽을 시간이 부족할 수 있습니다" : `확인기한 D-${dd}` });
  return list;
});
</script>

<style scoped>
.pv-banner { margin-bottom: var(--sw-space-3); }
.pv-toolbar { display: flex; align-items: center; gap: var(--sw-space-2); margin-bottom: var(--sw-space-3); }
.pv-label { font-size: var(--sw-font-sm); color: var(--el-text-color-secondary); }
.pv-checks { display: flex; flex-wrap: wrap; gap: var(--sw-space-2) var(--sw-space-4); font-size: var(--sw-font-sm); }
.pv-check { display: flex; align-items: center; gap: var(--sw-space-1); }
.pv-check.ok { color: var(--el-color-success); }
.pv-check.bad { color: var(--el-color-danger); }
.pv-screen { padding: var(--sw-space-3); background: var(--el-fill-color-lighter); border: 1px dashed var(--el-border-color); border-radius: 4px; }
.info-card { margin-bottom: var(--sw-space-3); }
.info-card h3 { margin: 0; font-size: var(--sw-font-lg); }
.pv-doc { width: 100%; height: 420px; border: none; background: #fff; }
.pv-docinfo { display: flex; align-items: center; gap: var(--sw-space-1); margin-bottom: var(--sw-space-2); font-size: var(--sw-font-sm); word-break: break-all; }
.pv-docinfo.ok { color: var(--el-color-success); }
.pv-docinfo.warn { color: var(--el-color-warning); }
.pv-docinfo.bad { color: var(--el-color-danger); }
.pv-docinfo.info { color: var(--el-text-color-secondary); }
.pv-nofile { padding: var(--sw-space-5); text-align: center; color: var(--el-text-color-secondary); }
.pv-readstatus { display: flex; align-items: center; gap: var(--sw-space-3); padding-top: var(--sw-space-3); }
.pv-readtime { font-size: var(--sw-font-sm); color: var(--el-text-color-secondary); }
.pv-consent-item { margin-bottom: var(--sw-space-2); }
.pv-sig { margin: var(--sw-space-3) 0; padding: var(--sw-space-3); background: var(--el-fill-color-light); border-radius: 4px; }
.pv-sig-title { font-weight: 600; margin-bottom: var(--sw-space-1); }
.pv-sig-box { max-width: 480px; aspect-ratio: 3 / 1; border: 1px dashed var(--el-border-color-darker); background: #fff; display: flex; align-items: center; justify-content: center; color: var(--el-text-color-placeholder); font-size: var(--sw-font-sm); }
.pv-hint { margin-top: var(--sw-space-1); font-size: var(--sw-font-xs); color: var(--el-text-color-secondary); }
.pv-errors { margin: var(--sw-space-3) 0; }
.pv-errlist { margin: 0; padding-left: var(--sw-space-4); }
.pv-actions { display: flex; gap: var(--sw-space-3); }
</style>
